import type { TourDefinition, TourStep } from "@/config/tours";

export const TRAINING_SEARCH_TOUR = "training-search";
export const TRAINING_BOOK_APPOINTMENT_TOUR = "training-book-appointment";
export const TRAINING_RESCHEDULE_TOUR = "training-reschedule";
export const TRAINING_PROVIDER_CALENDAR_TOUR = "training-provider-calendar";
export const TRAINING_CREATE_SHIFT_TOUR = "training-create-shift";
export const TRAINING_CHECK_IN_TOUR = "training-check-in";
export const TRAINING_ROOM_PATIENT_TOUR = "training-room-patient";

// The tour covers the whole page, so it can't follow a click into a window or
// the patient panel: steps point at what is already on screen and say what to
// do next. Every tour's first step uses an anchor that renders in all calendar
// views (the "Show me" runner expands the calendar's left panel first).
// Columns only exist in Day and Week view, so steps using them come after a
// step that is always there.
const SEARCH = "#tour-train-search"; // components/navigation/sidebar-header.tsx
const CALENDAR_HEADER = "#tour-train-calendar-header"; // custom-calendar/calendar-header.tsx
const CALENDAR_COLUMNS = "#tour-train-calendar-columns"; // custom-calendar/calendar-view/header.tsx
const CREATE_EVENT = "#tour-train-create-event"; // check-in/calendar/calendar-sidebar.tsx
const PROVIDER_FILTER = "#tour-train-provider-filter"; // check-in/calendar/calendar-sidebar.tsx
const VIEW_MODE = "#tour-train-view-mode"; // check-in/calendar/view-mode-select.tsx
const WORKING_HOURS = "#show-shift-backgrounds"; // check-in/calendar/calendar-sidebar.tsx (Day/Week only)

function step(
  selector: string,
  side: TourStep["side"],
  title: string,
  content: string,
): TourStep {
  return {
    icon: null,
    title,
    content,
    selector,
    side,
    pointerPadding: 8,
    pointerRadius: 8,
  };
}

export const schedulingTrainingTours: TourDefinition[] = [
  {
    tour: TRAINING_SEARCH_TOUR,
    steps: [
      step(
        SEARCH,
        "right",
        "Open search",
        "Click Search, or press Cmd+K (Ctrl+K on Windows) from any screen.",
      ),
      step(
        SEARCH,
        "right",
        "Type a name",
        "Start typing your practice patient's name. You can also search by phone number or date of birth.",
      ),
      step(
        SEARCH,
        "right",
        "Open the patient",
        "Click the patient in the results, or use the arrow keys and press Enter, to open their chart.",
      ),
      step(
        SEARCH,
        "right",
        "Jump to screens too",
        "Search also finds screens like Calendar or Settings, and shows your recent patients before you type.",
      ),
    ],
  },
  {
    tour: TRAINING_BOOK_APPOINTMENT_TOUR,
    steps: [
      step(
        CALENDAR_HEADER,
        "bottom",
        "Go to today",
        "Use the arrows or the date picker to show today, so you can check your practice patient in afterwards.",
      ),
      step(
        CREATE_EVENT,
        "right",
        "Click Create Event",
        "Click Create Event and choose Appointment.",
      ),
      step(
        VIEW_MODE,
        "right",
        "Use Day or Week view",
        "Appointments show in a column per provider in Day and Week view.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Or click an empty time",
        "Each column is a provider. Click an open time under one of them and choose Appointment to start at that time.",
      ),
      step(
        CREATE_EVENT,
        "right",
        "Fill in the details",
        "On the next screen, search for your practice patient, pick a provider, add a service and save.",
      ),
    ],
  },
  {
    tour: TRAINING_RESCHEDULE_TOUR,
    steps: [
      step(
        CALENDAR_HEADER,
        "bottom",
        "Go to the appointment's day",
        "Use the arrows or the date picker to find the day of your practice patient's appointment.",
      ),
      step(
        VIEW_MODE,
        "right",
        "Use Day or Week view",
        "Moving appointments works in Day and Week view.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Drag it to a new time",
        "Drag the appointment to another time or provider. Decoda asks you to confirm and can text the patient the new time.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Or change the start time",
        "You can also right-click the appointment, choose Edit appointment, change the start time and save.",
      ),
      step(
        CALENDAR_HEADER,
        "bottom",
        "Every change is recorded",
        "Decoda keeps a record of the old and new time on the appointment, so anyone can see what changed.",
      ),
    ],
  },
  {
    tour: TRAINING_PROVIDER_CALENDAR_TOUR,
    steps: [
      step(
        PROVIDER_FILTER,
        "right",
        "Show just your schedule",
        "Pick yourself here to hide the other providers' columns.",
      ),
      step(
        VIEW_MODE,
        "right",
        "Switch between day and week",
        "Choose Day to see one day in detail, or Week to see your whole week at once.",
      ),
      step(
        CALENDAR_HEADER,
        "bottom",
        "Move between days",
        "Use the arrows to step forward or back, or the date picker to jump to a date.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Open an appointment",
        "Click any appointment to open the patient's details and today's checklist on the right.",
      ),
    ],
  },
  {
    tour: TRAINING_CREATE_SHIFT_TOUR,
    steps: [
      step(
        VIEW_MODE,
        "right",
        "Use Day view",
        "Shifts are added from Day view, so switch to Day here if you're on another view.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Hover over a provider's name",
        "Hover over the name at the top of a provider's column and choose Add shift. If you don't see it, ask an admin for access.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Set the shift",
        "Choose who is working, the location and the start and end times, then save.",
      ),
      step(
        WORKING_HOURS,
        "right",
        "See working hours",
        "With Show Working Hours on, times outside someone's shift are striped, so everyone can see when they're working.",
      ),
    ],
  },
  {
    tour: TRAINING_CHECK_IN_TOUR,
    steps: [
      step(
        CALENDAR_HEADER,
        "bottom",
        "Go to today",
        "Make sure the calendar is showing today.",
      ),
      step(
        VIEW_MODE,
        "right",
        "Use Day or Week view",
        "Appointments show in a column per provider in Day and Week view.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Open the appointment",
        "Find your practice patient's appointment and click it. Their details open on the right.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Tick Checked In",
        "Under today's appointment on the right, tick Checked In on its checklist.",
      ),
      step(
        CALENDAR_HEADER,
        "bottom",
        "Everyone can see they're here",
        "The appointment changes colour on the calendar, so the whole team knows the patient has arrived.",
      ),
    ],
  },
  {
    tour: TRAINING_ROOM_PATIENT_TOUR,
    steps: [
      step(
        CALENDAR_HEADER,
        "bottom",
        "Go to today",
        "Make sure the calendar is showing today.",
      ),
      step(
        VIEW_MODE,
        "right",
        "Use Day or Week view",
        "Appointments show in a column per provider in Day and Week view.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Open the appointment",
        "Find your practice patient's appointment and click it. Their details open on the right.",
      ),
      step(
        CALENDAR_COLUMNS,
        "bottom",
        "Tick With MA",
        "On the appointment's checklist, tick With MA (or With Provider) when you take the patient back.",
      ),
      step(
        CALENDAR_HEADER,
        "bottom",
        "Providers see who's ready",
        "The appointment changes colour, so providers can see at a glance who is ready for them.",
      ),
    ],
  },
];
